import React, { useContext, useEffect, useRef, useState } from "react";
import SidebarContext from "../../contexts/MyContext";
import { AudioHandler } from "../../Apihander/AudioRecording/AudioRecording";
import { errorToast, successToast } from "../../utils/notificationUtils";
import MyNotes from "./MyNotes";
import RecordWithNotes from "./RecordWithNotes";
import AddNotes from "./AddNotes";
import { readErrorMessage, withNotes } from "./notesShared";
import "./notes.css";

// Held in the shared aiToolBusy, so an import outlives this screen and AI Tools can show it.
export const NOTES_IMPORT_BUSY = "notes-import";

// The AI Tools notes flow: My Notes, Record with notes and Add notes, with Back returning to the previous screen.
const NotesTool = ({ onClose, importExtensions, readDuration, uploadImportedFile }) => {
    const {
        filterAudioData, setFilterAudioData, setFavoriteAudio, folders,
        aiToolBusy, setAiToolBusy, setAudioListRefreshKey,
    } = useContext(SidebarContext);
    const [stack, setStack] = useState([{ id: "myNotes" }]);
    const importing = aiToolBusy === NOTES_IMPORT_BUSY;
    // An upload can finish after this screen is gone, so it checks before navigating here.
    const mountedRef = useRef(true);
    // The main list leaves out recordings inside folders, so those are read per folder.
    const [folderAudio, setFolderAudio] = useState([]);
    const [loadingFolders, setLoadingFolders] = useState(false);
    const importInputRef = useRef(null);

    const liveFolders = Array.isArray(folders) ? folders.filter((folder) => folder?._id && !folder.isDeleted) : [];
    const folderKey = liveFolders.map((folder) => folder._id).join(",");
    const folderNames = Object.fromEntries(liveFolders.map((folder) => [folder._id, folder.name]));

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    useEffect(() => {
        if (!liveFolders.length) {
            setFolderAudio([]);
            return undefined;
        }
        let cancelled = false;
        setLoadingFolders(true);
        Promise.all(liveFolders.map((folder) => AudioHandler.doGetFolderRecording(folder._id)))
            .then((responses) => {
                if (cancelled) return;
                setFolderAudio(responses.flatMap((response) => (Array.isArray(response?.data?.audio) ? response.data.audio : [])));
            })
            .finally(() => { if (!cancelled) setLoadingFolders(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [folderKey]);

    const rootAudio = Array.isArray(filterAudioData) ? filterAudioData.filter((audio) => audio?._id) : [];
    const rootIds = new Set(rootAudio.map((audio) => audio._id));
    const recordings = [...rootAudio, ...folderAudio.filter((audio) => audio?._id && !rootIds.has(audio._id))];
    const screen = stack[stack.length - 1];

    const push = (entry) => setStack((prev) => [...prev, entry]);
    const goBack = () => (stack.length > 1 ? setStack((prev) => prev.slice(0, -1)) : onClose());

    // Upload responses carry note ids only, so the list is read back to get the full notes.
    const refreshRecordings = async () => {
        const response = await AudioHandler.doGetFilterRecording();
        const audio = response?.data?.audio;
        if (!Array.isArray(audio)) return null;
        setFilterAudioData(audio);
        setFavoriteAudio(audio.filter((item) => item?.isFavourite === true));
        return audio;
    };

    const updateNotes = (audioId, updater) => {
        const apply = (list) =>
            Array.isArray(list)
                ? list.map((audio) => (audio?._id === audioId ? withNotes(audio, updater(audio.notes || [])) : audio))
                : list;
        setFilterAudioData(apply);
        setFavoriteAudio(apply);
        setFolderAudio(apply);
    };

    const openAddNotes = (audio, startAt) => push({ id: "addNotes", audioId: audio._id, fallback: audio, startAt });

    const handleRecordingSaved = async () => {
        await refreshRecordings();
        // Back on My Notes, where the new notes now show.
        setStack((prev) => prev.filter((entry) => entry.id !== "recordNotes"));
    };

    // An imported file becomes a normal recording, then opens straight on its notes screen.
    const handleImportChange = async (e) => {
        const file = e.target.files?.[0];
        // Cleared so picking the same file again still fires a change event.
        e.target.value = "";
        if (!file) return;

        const extension = file.name.split(".").pop()?.toLowerCase();
        if (!importExtensions.includes(extension)) {
            errorToast(`Please upload an audio file (${importExtensions.join(", ")})`);
            return;
        }
        if (aiToolBusy) return;
        setAiToolBusy(NOTES_IMPORT_BUSY);
        try {
            const duration = await readDuration(file);
            const uploaded = await uploadImportedFile(file, duration);
            if (uploaded?.statusCode !== 200) {
                errorToast(readErrorMessage(uploaded));
                return;
            }
            if (!mountedRef.current) {
                // Whichever list is on screen now reloads, so the recording shows up there.
                setAudioListRefreshKey((key) => key + 1);
                successToast(`"${file.name}" imported. Open AI Tools › My Notes to add notes to it.`);
                return;
            }
            successToast("Audio imported");
            const list = await refreshRecordings();
            const saved = list?.find((audio) => audio?._id === uploaded?.data?._id) || uploaded?.data;
            if (mountedRef.current) openAddNotes(saved);
        } catch (error) {
            errorToast(readErrorMessage(error));
        } finally {
            setAiToolBusy(null);
        }
    };

    let content = null;
    if (screen.id === "myNotes") {
        content = (
            <MyNotes
                recordings={recordings}
                folderNames={folderNames}
                loadingFolders={loadingFolders}
                importing={importing}
                onBack={goBack}
                onOpenNote={openAddNotes}
                onOpenRecording={(audio) => openAddNotes(audio)}
                onRecord={() => push({ id: "recordNotes" })}
                onPickRecording={() => { if (!aiToolBusy) importInputRef.current?.click(); }}
            />
        );
    } else if (screen.id === "recordNotes") {
        content = <RecordWithNotes onSaved={handleRecordingSaved} />;
    } else if (screen.id === "addNotes") {
        const audio = recordings.find((item) => item._id === screen.audioId) || screen.fallback;
        content = (
            <AddNotes
                key={`${screen.audioId}-${stack.length}`}
                audio={audio}
                startAt={screen.startAt}
                onBack={goBack}
                onNotesChange={updateNotes}
            />
        );
    }

    return (
        <div className="nt vr-tone-amber">
            <input
                type="file"
                ref={importInputRef}
                accept={importExtensions.map((ext) => `.${ext}`).join(",")}
                style={{ display: "none" }}
                onChange={handleImportChange}
            />
            {content}
        </div>
    );
};

export default NotesTool;
